// ============================================================================
// Filtriranje namirnica koje klijent ne voli.
//
// Pri sastavljanju obroka ne nude se:
//  - namirnice koje su u kartonu obelezene kao "hrana koju ne voli"
//  - recepti koji sadrze bilo koju od tih namirnica
//
// Poredjenje ne gleda velika/mala slova ni suvisne razmake. Namirnica se
// smatra pogodjenom ako se naziv iz kartona poklapa sa nazivom namirnice,
// ili se javlja kao CELA REC u njemu (npr. "brokoli" pogadja i
// "Brokoli smrznuti", ali "sir" ne pogadja "sirce").
// ============================================================================

// Skida razmake i velika slova; nasa slova ostaju kakva jesu
const normalize = (text) =>
    String(text == null ? '' : text).trim().toLowerCase().replace(/\s+/g, ' ');

// Deli naziv na reci - sve sto nije slovo ili cifra je granica reci
const toWords = (text) =>
    normalize(text).split(/[^0-9a-zà-ÿčćšžđ]+/i).filter((w) => w.length > 0);


// ----------------------------------------------------------------------------
// Prepoznavanje padeza (cvekla / cvekle / cveklom / cveklama).
//
// Rec se skracuje na koren tako sto se skidaju najcesci nastavci. Poredjenje
// se onda radi nad korenima, pa "cvekla" pogadja i "sok od cvekle".
//
// Koren mora ostati dovoljno dug (najmanje 3 slova), da se kratke reci
// (npr. "so") ne bi svele na jedno slovo i pogadjale sve zivo.
// ----------------------------------------------------------------------------

// Nastavci, od duzih ka kracim - uvek se skida prvi koji odgovara.
// min = koliko slova mora ostati posle skidanja tog nastavka.
//
// Nastavci sa "ij" (brokoli -> brokolija) traze duzi koren, jer bi inace
// "rakija" bila svedena na "rak" i pogadjala rakove.
// Nastavci -ov/-ev/-in se ovde NE skidaju; njih hvata withoutPossessive nize,
// da se ne bi ostetile reci kojima su ta slova deo same reci (bundeva, badem).
const SUFFIXES = [
    { suffix: 'ijama', min: 4 }, { suffix: 'ijima', min: 4 },
    { suffix: 'ijem', min: 4 },
    { suffix: 'ija', min: 4 }, { suffix: 'ije', min: 4 },
    { suffix: 'iju', min: 4 }, { suffix: 'iji', min: 4 },
    { suffix: 'ama', min: 3 }, { suffix: 'ima', min: 3 },
    { suffix: 'oga', min: 3 }, { suffix: 'ome', min: 3 }, { suffix: 'emu', min: 3 },
    { suffix: 'om', min: 3 }, { suffix: 'um', min: 3 },
    { suffix: 'em', min: 4 },
    { suffix: 'og', min: 3 }, { suffix: 'eg', min: 3 },
    { suffix: 'ih', min: 3 }, { suffix: 'im', min: 3 },
    { suffix: 'a', min: 3 }, { suffix: 'e', min: 3 },
    { suffix: 'i', min: 3 }, { suffix: 'o', min: 3 }, { suffix: 'u', min: 3 }
];

const MIN_STEM = 3;
const CONSONANT = 'bcčćdđfghjklmnpqrsštvzž';

// Skida nastavak i vraca koren reci
const stem = (word) => {
    const w = normalize(word);
    if (w.length <= MIN_STEM) return w;
    for (const rule of SUFFIXES) {
        if (w.length - rule.suffix.length >= rule.min && w.endsWith(rule.suffix))
            return w.slice(0, w.length - rule.suffix.length);
    }
    return w;
};

// "Nepostojano a": pirinač / pirinča, cvekala / cvekla.
// Uz koren dajemo i varijantu bez tog "a", pa se oba oblika prepoznaju.
const withoutFleetingA = (word) => {
    if (word.length < 4) return null;
    const a = word[word.length - 2];
    const before = word[word.length - 3];
    const after = word[word.length - 1];
    if (a === 'a' && CONSONANT.indexOf(before) !== -1 && CONSONANT.indexOf(after) !== -1)
        return word.slice(0, word.length - 2) + after;
    return null;
};

// Prisvojni oblik: bademovo -> badem, bundevine -> bundev, maslinovo -> maslin.
// To znaci "napravljeno od te namirnice", pa se racuna kao ista namirnica.
//
// Umanjenice (tikva / tikvice) se NAMERNO ne skidaju - to su cesto sasvim
// razlicite namirnice, pa bi ih skidanje pogresno izjednacilo.
const withoutPossessive = (word) => {
    for (const ending of ['ov', 'ev', 'in']) {
        if (word.length - ending.length >= MIN_STEM && word.endsWith(ending))
            return word.slice(0, word.length - ending.length);
    }
    return null;
};

// Svi oblici korena jedne reci koje poredimo
const stemVariants = (word) => {
    const base = stem(word);
    const variants = [base];

    const fleeting = withoutFleetingA(base);
    if (fleeting && fleeting.length >= MIN_STEM) variants.push(fleeting);

    const possessive = withoutPossessive(base);
    if (possessive && possessive.length >= MIN_STEM) variants.push(possessive);

    return variants;
};

// Da li dve reci imaju isti koren
const sameStem = (wordA, wordB) => {
    const a = stemVariants(wordA);
    const b = stemVariants(wordB);
    return a.some((x) => b.indexOf(x) !== -1);
};


// ----------------------------------------------------------------------------
// IZUZECI - kada naziv oznacava DRUGU namirnicu, pa se ne sklanja
//
// Primer: klijent ne voli masline, ali maslinovo ulje mu se i dalje nudi.
// Spiskovi ispod se mogu slobodno dopunjavati.
// ----------------------------------------------------------------------------

// Preradjevine - proizvod od namirnice, drugacijeg ukusa od nje same
const PRODUCT_WORDS = [
    'ulje', 'ulja', 'uljem',
    'sirće', 'sirceta', 'sirćeta',
    'puter', 'putera', 'puterom',
    'mleko', 'mleka', 'mlekom',
    'napitak', 'napitka',
    'semenke', 'semenki', 'seme', 'semena'
];

// Biljni izvori - oznacavaju da proizvod NIJE mlecni
const PLANT_SOURCE_WORDS = [
    'bademovo', 'bademova', 'bademovog', 'bademovim', 'badema',
    'sojino', 'sojin', 'sojina', 'sojinog', 'soje',
    'kokosovo', 'kokosova', 'kokosovog', 'kokosa',
    'ovseno', 'ovsena', 'ovsenog', 'ovsa',
    'pirinčano', 'pirinčana', 'pirinčanog',
    'lešnikovo', 'lešnika',
    'susamovo', 'susama',
    'konopljino', 'konoplje',
    'kikirikija', 'kikiriki',
    'indijskog', 'kešu'
];

// Mlecni proizvodi - njih biljne varijante ne smeju da sklanjaju
const DAIRY_WORDS = [
    'mleko', 'mleka', 'puter', 'putera', 'maslac', 'maslaca',
    'sir', 'sira', 'jogurt', 'jogurta', 'pavlaka', 'pavlake',
    'kajmak', 'kajmaka', 'surutka', 'surutke'
];

// Susena varijanta voca
const DRIED_WORDS = [
    'suvo', 'suva', 'suve', 'suvi', 'suvog', 'suvih',
    'sušeno', 'sušena', 'sušene', 'sušeni'
];

// Pojedinacni izuzeci - proizvodi koji se ne sklanjaju iako sadrze namirnicu.
// Ovde idu slucajevi koje opsta pravila ne pokrivaju.
// (Rec "sos" namerno nije u opstem spisku: sos od paradajza JESTE paradajz.)
const NAMED_EXCEPTIONS = [
    { source: 'soja', product: 'sojin sos' },
    { source: 'soja', product: 'sos od soje' }
];

const containsAny = (words, list) => words.some((w) => list.indexOf(w) !== -1);

// Da li naziv oznacava drugu namirnicu od one koju klijent ne voli.
//
// strict = rezim za ALERGIJE i INTOLERANCIJE. Tada se priznaje samo pravilo
// "biljna varijanta nije mlecni proizvod" (alergija na mleko ne sme da skloni
// bademovo mleko - to je upravo zamena koja se trazi), dok se preradjevine i
// susene varijante NE izuzimaju: alergija na badem mora da skloni i bademovo
// ulje i bademovo mleko.
const isDifferentProduct = (name, disliked, strict) => {
    const nameWords = toWords(name);
    const dislikedWords = toWords(disliked);

    // 1. mlecni proizvod naspram biljne varijante - vazi i u strogom rezimu
    if (containsAny(dislikedWords, DAIRY_WORDS) && containsAny(nameWords, PLANT_SOURCE_WORDS))
        return true;

    // kod alergija se dalje ne izuzima nista
    if (strict) return false;

    // 0. pojedinacni izuzeci (npr. sojin sos)
    for (const rule of NAMED_EXCEPTIONS) {
        if (normalize(name).indexOf(rule.product) !== -1
            && dislikedWords.some((w) => sameStem(w, rule.source)))
            return true;
    }

    // Pozicija same namirnice u nazivu (prva rec koja joj po korenu odgovara).
    // -1 znaci da se sama namirnica ne javlja kao rec (samo kroz pridev, npr.
    // "bademovo mleko" -> "badem" se ne javlja samostalno).
    const foodPos = nameWords.findIndex((nw) =>
        dislikedWords.some((dw) => stem(nw) === stem(dw)));

    // 2. preradjevina od namirnice (ulje, mleko, sirce, puter...)
    //    "ne voli masline" ne sme da skloni "maslinovo ulje".
    //    Ako se sama namirnica javlja kao rec (badem), naziv je ta namirnica
    //    sa dodatkom pa se ipak sklanja - OSIM kad je ispred nje predlog "od"
    //    ("puter od kikirikija"), sto je opet preradjevina.
    const foodAfterOd = foodPos > 0 && nameWords[foodPos - 1] === 'od';
    if ((foodPos < 0 || foodAfterOd)
        && containsAny(nameWords, PRODUCT_WORDS) && !containsAny(dislikedWords, PRODUCT_WORDS))
        return true;

    // 3. susena varijanta: opisna rec "suvo/suseno" mora biti ISPRED namirnice
    //    ("suvo grozdje"), da se ne bi okinula na "Badem (suvi...)" gde je opis IZA.
    const driedPos = nameWords.findIndex((nw) => DRIED_WORDS.indexOf(nw) !== -1);
    if (driedPos !== -1 && (foodPos === -1 || driedPos < foodPos)
        && !containsAny(dislikedWords, DRIED_WORDS))
        return true;

    return false;
};

// Spisak nevoljenih namirnica iz kartona klijenta.
// Uzima novu listu, a ako je prazna, pada nazad na stari slobodan upis
// (koji je razdvojen zarezima).
export const getDislikedNames = (client) => {
    if (!client) return [];
    const fromList = Array.isArray(client.dislikedFoodList) ? client.dislikedFoodList : [];
    const names = fromList.length > 0
        ? fromList
        : String(client.dislikesFood || '').split(',');
    return names
        .map((n) => normalize(n))
        .filter((n) => n.length > 0);
};

// Da li naziv namirnice pogadja neku od nevoljenih stavki
export const matchesDisliked = (ingredientName, dislikedNames, strict) => {
    const name = normalize(ingredientName);
    if (!name || !dislikedNames || dislikedNames.length === 0) return false;

    const words = toWords(name);
    return dislikedNames.some((disliked) => {
        if (!disliked) return false;
        if (name === disliked) return true;

        // naziv oznacava drugu namirnicu (maslinovo ulje, bademovo mleko, suvo grozdje)
        if (isDifferentProduct(name, disliked, strict)) return false;

        const dislikedWords = toWords(disliked);
        if (dislikedWords.length === 0) return false;

        // vise reci ("puter od kikirikija") - trazimo taj niz reci redom
        if (dislikedWords.length > 1) {
            for (let i = 0; i + dislikedWords.length <= words.length; i++) {
                let all = true;
                for (let j = 0; j < dislikedWords.length; j++) {
                    if (!sameStem(words[i + j], dislikedWords[j])) { all = false; break; }
                }
                if (all) return true;
            }
            return false;
        }

        // jedna rec - mora da se javi kao cela rec (u bilo kom padezu)
        return words.some((w) => sameStem(w, dislikedWords[0]));
    });
};

// Da li stavka iz ponude (namirnica ili recept) sadrzi nevoljenu namirnicu
export const optionHasDisliked = (option, dislikedNames, strict) => {
    if (!option || !dislikedNames || dislikedNames.length === 0) return false;

    // pojedinacna namirnica
    if (option.ingredient && matchesDisliked(option.ingredient.name || option.ingredient.label, dislikedNames, strict))
        return true;

    // recept - proveravamo sve njegove sastojke
    if (Array.isArray(option.recipeItems)) {
        for (const item of option.recipeItems) {
            if (!item) continue;
            const itemName = (item.ingredient && (item.ingredient.name || item.ingredient.label)) || item.label;
            if (matchesDisliked(itemName, dislikedNames, strict))
                return true;
        }
    }

    // stavka bez sastojaka - gledamo sam naziv
    if (!option.ingredient && !Array.isArray(option.recipeItems))
        return matchesDisliked(option.label || option.value, dislikedNames, strict);

    return false;
};

// Ponuda bez nevoljenih namirnica i recepata koji ih sadrze
export const filterOptions = (options, dislikedNames) => {
    if (!Array.isArray(options)) return [];
    if (!dislikedNames || dislikedNames.length === 0) return options;
    return options.filter((option) => !optionHasDisliked(option, dislikedNames));
};

// Nazivi nevoljenih stavki onako kako su upisani u kartonu (za prikaz)
export const getDislikedLabels = (client) => {
    if (!client) return [];
    const fromList = Array.isArray(client.dislikedFoodList) ? client.dislikedFoodList : [];
    const names = fromList.length > 0
        ? fromList
        : String(client.dislikesFood || '').split(',');
    return names.map((n) => String(n).trim()).filter((n) => n.length > 0);
};

// ============================================================================
// ALERGIJE I INTOLERANCIJE
//
// Strože od "ne voli": blokiraju se svi oblici i sve prerađevine namirnice.
//  - namirnica koja je alergen se uopšte ne nudi
//  - recept koji je sadrži se i dalje nudi, ali uz upozorenje da tu namirnicu
//    treba zameniti
// ============================================================================

// Alergeni iz kartona: alergije + intolerancije.
// Uzimaju se nove liste, a ako ih nema, stari slobodni upisi.
export const getAllergenNames = (client) => {
    if (!client) return [];
    const collect = (list, legacy) => {
        const fromList = Array.isArray(list) ? list : [];
        return fromList.length > 0 ? fromList : String(legacy || '').split(',');
    };
    const all = [
        ...collect(client.allergyList, client.allergiesString),
        ...collect(client.intoleranceList, client.foodIntolerance),
    ];
    const result = [];
    all.forEach((n) => {
        const name = normalize(n);
        if (name && result.indexOf(name) === -1) result.push(name);
    });
    return result;
};

// Nazivi alergena onako kako su upisani u kartonu (za prikaz)
export const getAllergenLabels = (client) => {
    if (!client) return [];
    const collect = (list, legacy) => {
        const fromList = Array.isArray(list) ? list : [];
        return fromList.length > 0 ? fromList : String(legacy || '').split(',');
    };
    const all = [
        ...collect(client.allergyList, client.allergiesString),
        ...collect(client.intoleranceList, client.foodIntolerance),
    ];
    const result = [];
    all.forEach((n) => {
        const name = String(n).trim();
        if (name && result.indexOf(name) === -1) result.push(name);
    });
    return result;
};

// Da li je stavka pojedinačna namirnica (a ne recept)
const isGrocery = (option) => !!option && !Array.isArray(option.recipeItems);

// Nazivi sastojaka u receptu koji su alergeni - to su oni koje treba zameniti
export const getAllergenIngredients = (option, allergenNames) => {
    if (!option || !allergenNames || allergenNames.length === 0) return [];
    const found = [];
    const add = (name) => {
        const clean = String(name || '').trim();
        if (clean && found.indexOf(clean) === -1) found.push(clean);
    };

    if (Array.isArray(option.recipeItems)) {
        option.recipeItems.forEach((item) => {
            if (!item) return;
            const itemName = (item.ingredient && (item.ingredient.name || item.ingredient.label)) || item.label;
            if (matchesDisliked(itemName, allergenNames, true)) add(itemName);
        });
    }
    if (option.ingredient) {
        const name = option.ingredient.name || option.ingredient.label;
        if (matchesDisliked(name, allergenNames, true)) add(name);
    }
    return found;
};

// Sklanja pojedinačne namirnice koje su alergeni.
// Recepti ostaju u ponudi - oni se mogu prilagoditi zamenom sastojka.
export const removeAllergenGroceries = (options, allergenNames) => {
    if (!Array.isArray(options)) return [];
    if (!allergenNames || allergenNames.length === 0) return options;
    return options.filter((option) => {
        if (!isGrocery(option)) return true;
        return !optionHasDisliked(option, allergenNames, true);
    });
};

// Da li recept sadrži alergen (za upozorenje u ponudi)
export const recipeHasAllergen = (option, allergenNames) => {
    if (!option || isGrocery(option)) return false;
    return getAllergenIngredients(option, allergenNames).length > 0;
};
